/**
 * SweepStatusBlock — BLU-461 (Phase B sync architecture pivot).
 *
 * Lives at the TOP of `ProductDataLookupCard.tsx`'s body, above the mapping
 * rows. Replaces the dropped per-row Re-sync UI affordance. Three operator-
 * visible phases driven server-side by `org_product_metadata_sync_state` +
 * the in-memory manual-sweep mutex:
 *
 *   - Idle      — "Last sync swept: 4 min ago" + [Sweep now] button (admin
 *                 only). Coordinators see the status text but no button.
 *   - In-progress — "Sweep started 12 sec ago. Scanned N products." Polled
 *                   every 5s via `getSweepStatus`.
 *   - Just-finished — "Sweep finished. N products scanned." Auto-collapses
 *                     to Idle after 8s (matches server-side
 *                     `JUST_FINISHED_WINDOW_MS`).
 *   - Partial-fail (within just_finished window) — Amber palette; surfaces
 *                  failure count + the last per-product error string.
 *
 * Hidden entirely when `mappings.length === 0` (frontend P0-4) — the block
 * lives inside the card's body and the caller decides whether to render it
 * based on the mapping list state.
 *
 * Implementation notes (PLAN §5 v1.1):
 *   - Reuses `useStaggeredTestPhase` motion constants (150 / 50 / 200 ms)
 *     so the click → spinner → status-band sequence matches the existing
 *     TestButton vocabulary.
 *   - All counters use `tabular-nums` to avoid digit jitter while polling.
 *   - `role="status" aria-live="polite"` on the status text region.
 *   - `type="button"` because the parent OrganizationSettings.tsx wraps
 *     this card in a global `<form>` — submit would fire the org-update
 *     mutation by accident.
 *   - Errors land via a persistent toast (id-deduplicated) — the inline
 *     status block stays operational so the operator can read the last
 *     successful sweep timestamp.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
// Plan 08 — semantic band palette single-sourced in ResultBand (closes the
// P2 follow-up filed below when these tokens were inlined). The in-progress
// blue stays local: it is a sweep-specific phase colour, not one of the four
// result-band kinds.
import { BAND_PALETTE } from './ResultBand';

const TOAST_ID = {
  sweepError: 'mapping-sweep-error',
  alreadyRunning: 'mapping-sweep-already-running',
  rateLimited: 'mapping-sweep-rate-limited',
} as const;

// Poll cadence while the server reports phase=in_progress. 5s matches the
// PLAN §5.1 contract and keeps the operator's status text "live enough"
// without burning ratchets — pure DB read on the backend, no Shopify.
const POLL_INTERVAL_MS = 5_000;

// Relative-time labels ("4 min ago") refresh every 30s so they don't sit
// stale (PLAN §5.5 v1.1 P1-1). Cheap interval; bounded by card mount.
const RELATIVE_TIME_TICK_MS = 30_000;

// Spinner-cool delay after the server returns success for `runSweepNow`,
// before invalidating `getSweepStatus`. Matches the canonical motion stagger
// (MOTION_SPINNER_FADE_MS in `useStaggeredTestPhase.ts`) so the click sequence
// reads as: spinner cools (150ms) → status block fades in (200ms). Without
// this, the in_progress block snaps in instantly and the click feels jarring
// next to the Test button which DOES stage. Post-impl frontend P1-1 fix.
const SPINNER_COOL_DELAY_MS = 150;

interface SweepStatusBlockProps {
  /** True for org admins; controls visibility of the Sweep now button. */
  admin: boolean;
}

export function SweepStatusBlock({ admin }: SweepStatusBlockProps) {
  // Polling enables only while phase is `in_progress`. We don't poll on
  // mount; the first read drives whether polling kicks in.
  const [isPolling, setIsPolling] = useState(false);

  const statusQuery = trpc.shopifyMetafieldMappings.getSweepStatus.useQuery(
    undefined,
    {
      staleTime: 10_000,
      refetchInterval: isPolling ? POLL_INTERVAL_MS : false,
      // Refetch on window focus is fine — operator returning to the tab
      // wants the freshest status.
      refetchOnWindowFocus: true,
    },
  );

  // React to phase transitions: turn polling on when in_progress; off
  // otherwise. The 5s polling cadence is the visible cue that the sweep
  // is alive.
  const phase = statusQuery.data?.phase ?? 'idle';
  useEffect(() => {
    setIsPolling(phase === 'in_progress');
  }, [phase]);

  const utils = trpc.useUtils();

  const sweepMutation = trpc.shopifyMetafieldMappings.runSweepNow.useMutation({
    onSuccess: (result) => {
      if (result.alreadyRunning) {
        // `toast.warning` (not `toast.message`) so the icon + color signal
        // "user-correctable guardrail" rather than a neutral notification
        // (post-impl frontend P0-2).
        toast.warning(
          'A sweep is already running. Wait for it to finish before starting another.',
          { id: TOAST_ID.alreadyRunning, duration: 4_000 },
        );
        return;
      }
      // Don't fire a "started" toast — the inline status block transition
      // to in_progress IS the success cue (DESIGN.md §15.4). Delay the
      // invalidate by SPINNER_COOL_DELAY_MS so the click sequence stages
      // the same way the canonical TestButton does (post-impl frontend
      // P1-1 motion-drift fix).
      window.setTimeout(() => {
        void utils.shopifyMetafieldMappings.getSweepStatus.invalidate();
      }, SPINNER_COOL_DELAY_MS);
    },
    onError: (error) => {
      const isRateLimit =
        error.data?.code === 'TOO_MANY_REQUESTS' ||
        error.message.toLowerCase().includes('rate exceeded');
      if (isRateLimit) {
        // PLAN §5.2 v1.1 microcopy — replaces the raw server diagnostic
        // ("Sweep rate exceeded for this organization. Try again in ~58s.")
        // with operator-friendly copy. `toast.warning` for the same
        // semantic-color reason as alreadyRunning above (post-impl frontend
        // P0-1 + P0-2).
        toast.warning('Sweep was rate-limited. Try again in a minute.', {
          id: TOAST_ID.rateLimited,
          duration: 4_000,
        });
        return;
      }
      // Sweep failures get a PERSISTENT toast (no auto-dismiss). ID dedup
      // keeps repeated failures from stacking. Matches the existing
      // upsertError pattern at ProductDataLookupCard.tsx:222.
      toast.error(`Sweep failed: ${error.message}`, {
        id: TOAST_ID.sweepError,
        duration: Infinity,
      });
    },
  });

  // Force re-render every 30s so the "4 min ago" string stays fresh even
  // when no other state changes. The tick counter MUST be a dep of the
  // relative-time `useMemo`s below or they'll never recompute when nothing
  // else changes (post-impl correctness P1 — startedAgo was previously
  // memoized only on `lastSweepStartedAt` which is fixed mid-sweep).
  const [relativeTick, setRelativeTick] = useState(0);
  useEffect(() => {
    const interval = window.setInterval(() => {
      setRelativeTick((n) => n + 1);
    }, RELATIVE_TIME_TICK_MS);
    return () => window.clearInterval(interval);
  }, []);

  const data = statusQuery.data;

  // Derived display strings — memoized so render is cheap. The tick counter
  // is in the deps so the 30s interval drives an actual recompute (without
  // it, "4 min ago" would freeze the moment the data first lands).
  const lastSweptHumanReadable = useMemo(() => {
    if (!data?.lastSyncSweptAt) return null;
    return formatDistanceToNowStrict(parseISO(data.lastSyncSweptAt), {
      addSuffix: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- relativeTick is intentional
  }, [data?.lastSyncSweptAt, relativeTick]);

  const lastSweptAbsoluteIso = data?.lastSyncSweptAt ?? null;

  const startedAgo = useMemo(() => {
    if (!data?.lastSweepStartedAt) return null;
    return formatDistanceToNowStrict(parseISO(data.lastSweepStartedAt), {
      addSuffix: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- relativeTick is intentional
  }, [data?.lastSweepStartedAt, relativeTick]);

  // Click protection: disable during in-flight mutation OR while the
  // server-reported phase is in_progress.
  const buttonDisabled = sweepMutation.isPending || phase === 'in_progress';

  const handleClick = useCallback(() => {
    sweepMutation.mutate();
  }, [sweepMutation]);

  // Loading-skeleton state — first paint while the query is in flight.
  if (statusQuery.isLoading && !statusQuery.data) {
    return (
      <div
        className="motion-safe:animate-pulse motion-reduce:animate-none rounded-md border border-border bg-muted/20 px-3 py-2"
        aria-hidden="true"
      >
        <div className="h-4 w-48 rounded bg-muted" />
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Phase-specific rendering. Each branch returns its own block so the
  // motion-safe fade/slide entrance can fire on phase transition.
  // ---------------------------------------------------------------------

  if (phase === 'in_progress') {
    return (
      <InProgressBlock
        startedAgo={startedAgo}
        productsScanned={data?.lastSweepProductsScanned ?? null}
      />
    );
  }

  if (phase === 'just_finished') {
    const failed = data?.lastSweepProductsFailed ?? 0;
    if (failed > 0) {
      return (
        <JustFinishedPartialBlock
          productsScanned={data?.lastSweepProductsScanned ?? 0}
          productsFailed={failed}
          lastError={data?.lastSweepError ?? null}
        />
      );
    }
    return (
      <JustFinishedBlock
        productsScanned={data?.lastSweepProductsScanned ?? 0}
      />
    );
  }

  // ---------- idle ----------
  // Sticky partial-fail surface (post-impl frontend P0-3): when the most
  // recent sweep had failures, IdleBlock renders an amber variant that
  // persists indefinitely (across the 8s `just_finished` window) until the
  // NEXT sweep starts. Operators who looked away during the sweep can still
  // see "Last sweep had N failures" + View details. The visibility lifecycle
  // is: sweep finishes with failures → just_finished phase shows partial-fail
  // (8s) → phase collapses to idle → IdleBlock renders sticky amber variant
  // → next sweep starts → phase=in_progress → in_progress block.
  const lastSweepFailures = data?.lastSweepProductsFailed ?? 0;
  return (
    <IdleBlock
      admin={admin}
      lastSweptHumanReadable={lastSweptHumanReadable}
      lastSweptAbsoluteIso={lastSweptAbsoluteIso}
      buttonDisabled={buttonDisabled}
      buttonStarting={sweepMutation.isPending}
      onSweepClick={handleClick}
      lastSweepProductsFailed={lastSweepFailures}
      lastSweepProductsScanned={data?.lastSweepProductsScanned ?? 0}
      lastSweepError={data?.lastSweepError ?? null}
    />
  );
}

// ---------------------------------------------------------------------------
// Phase sub-components — each one renders its own animate-in transition so
// the operator sees a clean fade between phases without a layout jump.
// ---------------------------------------------------------------------------

interface IdleBlockProps {
  admin: boolean;
  lastSweptHumanReadable: string | null;
  lastSweptAbsoluteIso: string | null;
  buttonDisabled: boolean;
  buttonStarting: boolean;
  onSweepClick: () => void;
  /** When > 0, IdleBlock renders the sticky amber partial-fail variant. */
  lastSweepProductsFailed: number;
  lastSweepProductsScanned: number;
  lastSweepError: string | null;
}

function IdleBlock({
  admin,
  lastSweptHumanReadable,
  lastSweptAbsoluteIso,
  buttonDisabled,
  buttonStarting,
  onSweepClick,
  lastSweepProductsFailed,
  lastSweepProductsScanned,
  lastSweepError,
}: IdleBlockProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasFailures = lastSweepProductsFailed > 0;

  // Helper sentence reads more naturally when phrased to the actual state:
  // first-run (no prior sweep) gets a first-touch verb; subsequent runs get
  // the cadence helper. Post-impl frontend P1-5.
  const helperText = lastSweptHumanReadable
    ? 'Pulls the latest Shopify product changes and updates the print-file lookup table.'
    : 'Run a sweep to populate the print-file lookup table from your Shopify products.';

  // Amber tokens come from the shared BAND_PALETTE (Plan 08 — the P2
  // follow-up that was filed here when they were inlined). The palette
  // string carries the text colour too, so the amber branch's text class
  // just inherits.
  const containerClass = hasFailures
    ? `rounded-md border px-3 py-2 ${BAND_PALETTE.amber} motion-safe:animate-in motion-safe:fade-in-50 motion-reduce:animate-none motion-reduce:transition-none flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between`
    : 'motion-safe:animate-in motion-safe:fade-in-50 motion-reduce:animate-none motion-reduce:transition-none rounded-md border border-border bg-muted/20 px-3 py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between';

  const textClass = hasFailures ? 'text-sm' : 'text-sm text-muted-foreground';

  return (
    <div role="status" aria-live="polite" className={containerClass}>
      <div className={textClass}>
        {hasFailures ? (
          <>
            {lastSweptHumanReadable ? (
              <>
                <AlertTriangle
                  className="mr-1.5 inline h-3.5 w-3.5"
                  aria-hidden="true"
                />
                Last sweep{' '}
                <time
                  dateTime={lastSweptAbsoluteIso ?? undefined}
                  title={lastSweptAbsoluteIso ?? undefined}
                >
                  {lastSweptHumanReadable}
                </time>
                {': '}
                <span className="tabular-nums">
                  {lastSweepProductsFailed.toLocaleString()}
                </span>{' '}
                of{' '}
                <span className="tabular-nums">
                  {lastSweepProductsScanned.toLocaleString()}
                </span>{' '}
                failed.
              </>
            ) : (
              <>
                <AlertTriangle
                  className="mr-1.5 inline h-3.5 w-3.5"
                  aria-hidden="true"
                />
                Last sweep had{' '}
                <span className="tabular-nums">
                  {lastSweepProductsFailed.toLocaleString()}
                </span>{' '}
                failure{lastSweepProductsFailed === 1 ? '' : 's'}.
              </>
            )}
            {lastSweepError ? (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  className="underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded text-amber-900 dark:text-amber-100"
                  aria-expanded={detailsOpen}
                >
                  {detailsOpen ? 'Hide details' : 'View details'}
                </button>
              </>
            ) : null}
            {detailsOpen && lastSweepError ? (
              <p className="mt-2 rounded border border-amber-300 bg-amber-100 px-2 py-1 text-xs font-mono break-all dark:border-amber-800 dark:bg-amber-900/40">
                {lastSweepError}
              </p>
            ) : null}
          </>
        ) : (
          <p>
            {lastSweptHumanReadable ? (
              <>
                Last sync swept:{' '}
                <time
                  dateTime={lastSweptAbsoluteIso ?? undefined}
                  title={lastSweptAbsoluteIso ?? undefined}
                  className="text-foreground"
                >
                  {lastSweptHumanReadable}
                </time>
                .
              </>
            ) : (
              <>No sync swept yet.</>
            )}{' '}
            {helperText}
          </p>
        )}
      </div>
      {admin && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSweepClick}
          disabled={buttonDisabled}
          aria-label="Sweep all products through configured mappings"
          className="shrink-0 motion-safe:transition-all motion-reduce:transition-none"
        >
          {buttonStarting ? (
            <>
              <Loader2
                className="mr-2 h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              Starting&hellip;
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
              Sweep now
            </>
          )}
        </Button>
      )}
    </div>
  );
}

interface InProgressBlockProps {
  startedAgo: string | null;
  productsScanned: number | null;
}

function InProgressBlock({ startedAgo, productsScanned }: InProgressBlockProps) {
  // Key on phase so a fresh in_progress entry re-fires the aria-live
  // announcement — the screen-reader user gets one polite-message per
  // transition, not on every 5s poll tick.
  return (
    <div
      key="in_progress"
      role="status"
      aria-live="polite"
      className="motion-safe:animate-in motion-safe:fade-in-50 motion-safe:slide-in-from-bottom-1 motion-reduce:animate-none motion-reduce:transition-none rounded-md border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900/40 dark:bg-blue-950/30"
    >
      <p className="text-sm text-blue-900 dark:text-blue-100">
        <Loader2
          className="mr-1.5 inline h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
        Sweep in progress
        {startedAgo ? (
          <>
            {' '}
            (started <span className="tabular-nums">{startedAgo}</span> ago)
          </>
        ) : null}
        {productsScanned !== null && productsScanned > 0 ? (
          <>
            . Scanned{' '}
            <span className="tabular-nums">{productsScanned.toLocaleString()}</span>{' '}
            products so far.
          </>
        ) : (
          '.'
        )}
      </p>
    </div>
  );
}

interface JustFinishedBlockProps {
  productsScanned: number;
}

function JustFinishedBlock({ productsScanned }: JustFinishedBlockProps) {
  return (
    <div
      key="just_finished"
      role="status"
      aria-live="polite"
      className={`motion-safe:animate-in motion-safe:fade-in-50 motion-safe:slide-in-from-bottom-1 motion-reduce:animate-none motion-reduce:transition-none rounded-md border px-3 py-2 ${BAND_PALETTE.green}`}
    >
      <p className="text-sm">
        <CheckCircle2 className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
        Sweep finished.{' '}
        <span className="tabular-nums">{productsScanned.toLocaleString()}</span>{' '}
        products scanned.
      </p>
    </div>
  );
}

interface JustFinishedPartialBlockProps {
  productsScanned: number;
  productsFailed: number;
  lastError: string | null;
}

function JustFinishedPartialBlock({
  productsScanned,
  productsFailed,
  lastError,
}: JustFinishedPartialBlockProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  return (
    <div
      key="just_finished_partial"
      role="status"
      aria-live="polite"
      className={`motion-safe:animate-in motion-safe:fade-in-50 motion-safe:slide-in-from-bottom-1 motion-reduce:animate-none motion-reduce:transition-none rounded-md border px-3 py-2 ${BAND_PALETTE.amber}`}
    >
      <p className="text-sm">
        <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
        Sweep finished with errors.{' '}
        <span className="tabular-nums">{productsFailed.toLocaleString()}</span>{' '}
        of{' '}
        <span className="tabular-nums">{productsScanned.toLocaleString()}</span>{' '}
        failed.
        {lastError ? (
          <>
            {' '}
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              className="underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded text-amber-900 dark:text-amber-100"
              aria-expanded={detailsOpen}
            >
              {detailsOpen ? 'Hide details' : 'View details'}
            </button>
          </>
        ) : null}
      </p>
      {detailsOpen && lastError ? (
        <p className="mt-2 rounded border border-amber-300 bg-amber-100 px-2 py-1 text-xs font-mono text-amber-900 dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-100 break-all">
          {lastError}
        </p>
      ) : null}
    </div>
  );
}
